<template>
  <DxPopup
    :visible="visible"
    :title="$t('stock_management.transactions.erp_response_title')"
    :show-close-button="true"
    :width="750"
    :height="600"
    @hidden="$emit('close')"
  >
    <template #content>
      <div>
        <div v-if="loading" class="erp-response-loading">
          <img src="~@/assets/images/icons/loader-points.gif" alt="Loading..." />
        </div>
        <div v-else-if="error" class="erp-response-error">
          <p>{{ $t("stock_management.transactions.erp_response_error") }}</p>
          <pre class="json-viewer">{{ formatJson(error.response && error.response.data ? error.response.data : error.message || error) }}</pre>
        </div>
        <div v-else-if="data" class="erp-response-content">
          <div class="erp-response-meta">
            <div v-if="!isTransferItem">
              <strong>{{ $t("tools.erp_integration.reception_wm_document") }}:</strong> {{ data.wm_document_number || '-' }}
            </div>
            <div>
              <strong>{{ $t("common.status") }}:</strong> {{ data.status || '-' }}
            </div>
            <div>
              <strong>{{ $t("tools.erp_integration.reception_attempts") }}:</strong> {{ data.attempt_count || 0 }}
            </div>
            <div v-if="data.last_error_code">
              <strong>{{ $t("tools.erp_integration.reception_last_error") }}:</strong> {{ data.last_error_code }}
            </div>
          </div>
          <div v-if="!isTransferItem && hasRawResponse" class="erp-response-raw">
            <label>{{ $t("stock_management.transactions.erp_response_raw") }}</label>
            <div class="erp-response-meta">
              <div>
                <strong>{{ $t("common.status") }}:</strong> {{ data.wm_http_status || '-' }} ({{ data.wm_result || '-' }})
              </div>
              <div>
                <strong>{{ $t("tools.erp_integration.reception_wm_document") }}:</strong>
                {{ data.wm_document_series || '-' }} / {{ data.wm_document_number || '-' }} ({{ data.wm_document_code || '-' }})
              </div>
            </div>
            <pre class="json-viewer">{{ formatJson(data.wm_response_json) }}</pre>
          </div>
          <template v-if="isTransferItem">
            <div v-for="section in transferRawSections" :key="section.key" class="erp-response-raw">
              <label>{{ section.label }}</label>
              <div class="erp-response-meta">
                <div>
                  <strong>{{ $t("tools.erp_integration.reception_wm_document") }}:</strong>
                  {{ section.series || '-' }} / {{ section.number || '-' }} ({{ section.code || '-' }})
                </div>
              </div>
              <pre class="json-viewer">{{ formatJson(section.response) }}</pre>
            </div>
          </template>
          <div v-if="lastEvent" class="erp-response-raw">
            <label>{{ $t("stock_management.transactions.erp_response_audit") }}</label>
            <div class="erp-response-meta">
              <div>
                <strong>{{ $t("common.created_at") }}:</strong> {{ lastEvent.created_at || '-' }}
              </div>
              <div>
                <strong>{{ $t("tools.erp_integration.reception_event_type") }}:</strong> {{ lastEvent.event_type || '-' }}
              </div>
              <div>
                <strong>{{ $t("tools.erp_integration.reception_previous_status") }}:</strong> {{ lastEvent.previous_status || '-' }}
                &rarr;
                <strong>{{ $t("tools.erp_integration.reception_new_status") }}:</strong> {{ lastEvent.new_status || '-' }}
              </div>
              <div v-if="lastEvent.message">
                <strong>{{ $t("common.message") }}:</strong> {{ lastEvent.message }}
              </div>
            </div>
          </div>
          <div v-if="!hasRawResponse && !hasTransferRawResponse && !lastEvent" class="erp-response-empty">
            {{ $t("stock_management.transactions.erp_response_no_events") }}
          </div>
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";

export default {
  name: "ErpResponsePopup",
  components: {
    DxPopup,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    row: {
      type: Object,
      default: null,
    },
    data: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object,
      default: null,
    },
  },
  computed: {
    // A queue item típusát a benne lévő mezők alapján ismerjük fel: a transfer
    // queue-nek 3 külön ERP hívása van (kiszállítás/bevételezés/transzfer),
    // szemben a recepció egyetlen wm_response_json mezőjével.
    isTransferItem() {
      return !!(
        this.data &&
        (Object.prototype.hasOwnProperty.call(this.data, "wm_response_iesiri_json") ||
          Object.prototype.hasOwnProperty.call(this.data, "wm_response_intrari_json") ||
          Object.prototype.hasOwnProperty.call(this.data, "wm_response_transfer_json"))
      );
    },
    // A nyers ERP válasz (WinMentor HTTP body) a queue itemen tárolódik, nem az
    // audit eseményeken - a backend minden sikeres/sikertelen küldés után ide
    // írja felül a legutolsó választ (lásd ErpReceptionExportService::sendQueueItem/markSendFailure).
    hasRawResponse() {
      return !!(this.data && this.data.wm_response_json);
    },
    transferRawSections() {
      if (!this.isTransferItem || !this.data) {
        return [];
      }

      return [
        {
          key: "iesiri",
          label: this.$t("stock_management.transactions.erp_response_transfer_iesiri"),
          series: this.data.wm_document_series_out,
          number: this.data.wm_document_number_out,
          code: this.data.wm_document_code_out,
          response: this.data.wm_response_iesiri_json,
        },
        {
          key: "intrari",
          label: this.$t("stock_management.transactions.erp_response_transfer_intrari"),
          series: this.data.wm_document_series_in,
          number: this.data.wm_document_number_in,
          code: this.data.wm_document_code_in,
          response: this.data.wm_response_intrari_json,
        },
        {
          key: "transfer",
          label: this.$t("stock_management.transactions.erp_response_transfer_transfer"),
          series: this.data.wm_document_series_transfer,
          number: this.data.wm_document_number_transfer,
          code: this.data.wm_document_code_transfer,
          response: this.data.wm_response_transfer_json,
        },
      ].filter((section) => section.response || section.number || section.series || section.code);
    },
    hasTransferRawResponse() {
      return this.transferRawSections.length > 0;
    },
    lastEvent() {
      const events = Array.isArray(this.data?.events) ? this.data.events : [];
      if (!events.length) {
        return null;
      }

      // Az utolsó esemény a legfrissebb ERP válasz (a lista created_at szerint nincs
      // garantáltan rendezve, ezért a legnagyobb created_at-ú elemet választjuk).
      return events.reduce((latest, current) => {
        if (!latest) {
          return current;
        }

        const latestTime = new Date(latest.created_at || 0).getTime();
        const currentTime = new Date(current.created_at || 0).getTime();
        return currentTime >= latestTime ? current : latest;
      }, null);
    },
  },
  methods: {
    formatJson(value) {
      if (typeof value === "string") {
        try {
          return JSON.stringify(JSON.parse(value), null, 2);
        } catch (e) {
          return value;
        }
      }

      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.json-viewer {
  background: #1e293b;
  color: #e2e8f0;
  padding: 14px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 12px;
  max-height: 450px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.erp-response-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;

  img {
    width: 40px;
    height: 40px;
  }
}

.erp-response-error {
  color: #d32f2f;

  p {
    font-weight: 600;
    margin-bottom: 8px;
  }
}

.erp-response-meta {
  margin-bottom: 16px;

  div {
    margin-bottom: 4px;
  }

  strong {
    min-width: 110px;
    display: inline-block;
  }
}

.erp-response-empty {
  color: #64748b;
  font-style: italic;
}

.erp-response-raw {
  label {
    display: block;
    font-weight: 600;
    margin-bottom: 6px;
  }
}
</style>